import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { toast } from 'sonner';
import { ArrowLeft, Barcode } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InventoryReceive({ user }) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions(user);
  
  // Check permission
  if (!hasPermission('inventory_receive')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to receive inventory.</p>
          <Button onClick={() => navigate('/production-unit')}>Go Back</Button>
        </div>
      </div>
    );
  }
  
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [executedPOs, setExecutedPOs] = useState([]);
  const [receivedPOIds, setReceivedPOIds] = useState([]); // Track POs that have been received
  const [selectedPO, setSelectedPO] = useState(null);
  const [poItems, setPoItems] = useState([]);
  const [receivingItems, setReceivingItems] = useState([]);
  
  // Search states
  const [barcodeSearch, setBarcodeSearch] = useState('');
  const [dropdownPO, setDropdownPO] = useState('');

  useEffect(() => {
    fetchReceivedPOs();
  }, []);

  useEffect(() => {
    if (receivedPOIds.length >= 0) { // Fetch even if empty array
      fetchPurchaseOrders();
      fetchExecutedPOs();
    }
  }, [receivedPOIds]);

  const fetchReceivedPOs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/inventory/receive-transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Extract unique PO IDs that have been received
      const receivedIds = [...new Set(response.data.map(tx => tx.po_id).filter(id => id))];
      setReceivedPOIds(receivedIds);
    } catch (error) {
      console.error('Error fetching received POs:', error);
    }
  };

  // Auto-refresh every 5 seconds to update received POs list
  useAutoRefresh(fetchReceivedPOs, 5, false);

  const fetchPurchaseOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter APPROVED/AUTHORIZED POs that have NOT been received yet
      const approvedPOs = response.data.filter(po => 
        (po.status === 'APPROVED' || po.status === 'AUTHORIZED') && !receivedPOIds.includes(po.id)
      );
      setPurchaseOrders(approvedPOs);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
    }
  };

  const fetchExecutedPOs = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching POs with token:', token ? 'Token exists' : 'NO TOKEN');
      
      const response = await axios.get(`${API}/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('API Response status:', response.status);
      console.log('All POs fetched:', response.data.length);
      
      // Log all PO statuses to see what we have
      console.log('PO Statuses:', response.data.map(po => ({
        po_ref: po.po_ref_number,
        status: po.status
      })));
      
      const allExecutedPOs = response.data.filter(po => po.status === 'EXECUTED');
      console.log('Total EXECUTED POs:', allExecutedPOs.length);
      
      if (allExecutedPOs.length === 0) {
        console.warn('⚠️ NO EXECUTED POs FOUND! Available statuses:', 
          [...new Set(response.data.map(po => po.status))]);
      }
      
      console.log('Executed POs:', allExecutedPOs);
      
      if (allExecutedPOs.length > 0) {
        console.log('First executed PO:', allExecutedPOs[0]);
      }
      
      setExecutedPOs(allExecutedPOs);
    } catch (error) {
      console.error('Error fetching executed POs:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error('Failed to fetch purchase orders. Please check console.');
    }
  };

  const handleSelectPO = async (poId) => {
    try {
      const token = localStorage.getItem('token');
      // Fetch FULL PO details by ID (same as InventoryIssue does for work orders)
      const response = await axios.get(`${API}/purchase-orders/${poId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const po = response.data;
      console.log('Fetched full PO:', po);
      
      setSelectedPO(po);
      
      // Purchase Orders store items directly in po.items (not article_data like Work Orders)
      // POItem structure: { code, color_code, color_name, quantity, unit_price, total_price }
      const items = [];
      
      if (po.items && Array.isArray(po.items)) {
        po.items.forEach(item => {
          items.push({
            code: item.code,
            color_code: item.color_code,
            color_name: item.color_name,
            quantity: item.quantity,
            unit_price: item.unit_price,
            total_price: item.total_price
          });
        });
      }
      
      console.log('Loaded PO items:', items);
      console.log('Items count:', items.length);
      
      // Check if items exist
      if (!items || items.length === 0) {
        toast.error('This PO has no items to receive');
        console.error('PO has no items:', po);
        return;
      }
      
      setPoItems(items);
      setReceivingItems(items.map(item => ({
        ...item,
        quantity_to_receive: ''
      })));
      
      console.log('PO loaded successfully with', items.length, 'items');
      toast.success(`PO ${po.po_ref_number} loaded with ${items.length} items`);
    } catch (error) {
      console.error('Error fetching PO details:', error);
      toast.error('Failed to fetch PO details');
    }
  };

  const handleBarcodeSearch = async (e) => {
    const value = e.target.value;
    setBarcodeSearch(value);
    
    // Clear other search fields
    if (value) {
      setDropdownPO('');
      
      // Auto-search if barcode has at least 2 digits
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length >= 2) {
        searchByBarcode(value);
      }
    }
  };

  const searchByBarcode = async (barcode) => {
    if (!barcode || barcode.trim() === '') {
      toast.error('Please enter a barcode');
      return;
    }

    console.log('Searching for barcode:', barcode);
    console.log('Total executedPOs:', executedPOs.length);
    console.log('Received PO IDs:', receivedPOIds);
    
    // Filter out already received POs
    const availablePOs = executedPOs.filter(po => !receivedPOIds.includes(po.id));
    console.log('Available executedPOs (not received):', availablePOs.length);

    // Extract numeric value from scanned barcode (e.g., "PO-00011" → "11", "00011" → "11", "11" → "11")
    const scannedNumber = parseInt(barcode.replace(/\D/g, ''), 10);
    
    if (isNaN(scannedNumber)) {
      toast.error('Invalid barcode format');
      return;
    }
    
    console.log('Searching for PO number:', scannedNumber);
    
    // Search in available executedPOs (not received)
    const po = availablePOs.find(p => {
      const poNumber = parseInt(p.po_ref_number.replace(/\D/g, ''), 10);
      console.log('Comparing', poNumber, 'with', scannedNumber);
      return poNumber === scannedNumber;
    });
    
    console.log('Found PO:', po);
    
    if (po) {
      // Check if PO has already been received
      if (receivedPOIds.includes(po.id)) {
        toast.error(`This PO has already been received against`);
        return;
      }
      
      // PO found in executed list, select it
      await handleSelectPO(po.id);
      toast.success(`PO ${po.po_ref_number} found and loaded!`);
    } else {
      // Check if PO exists in other statuses
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/purchase-orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const anyPO = response.data.find(p => {
          const poNumber = parseInt(p.po_ref_number.replace(/\D/g, ''), 10);
          return poNumber === scannedNumber;
        });
        
        if (anyPO) {
          toast.error(`PO found but status is ${anyPO.status}. Only EXECUTED POs can be received. Current status needs to be EXECUTED.`);
        } else {
          toast.error(`No PO found with number matching: ${scannedNumber}`);
        }
      } catch (error) {
        toast.error(`No executed PO found with number matching: ${scannedNumber}`);
      }
    }
  };

  const handleDropdownChange = (value) => {
    setDropdownPO(value);
    
    // Clear other search fields
    if (value) {
      setBarcodeSearch('');
    }
    
    if (value) {
      // Check if PO has already been received (extra safety check)
      if (receivedPOIds.includes(value)) {
        toast.error(`This PO has already been received against`);
        setDropdownPO('');
        return;
      }
      handleSelectPO(value);
    }
  };

  const clearSearch = () => {
    setBarcodeSearch('');
    setDropdownPO('');
    setSelectedPO(null);
    setPoItems([]);
    setReceivingItems([]);
  };

  const handleQuantityChange = (index, value) => {
    const updated = [...receivingItems];
    updated[index].quantity_to_receive = value;
    setReceivingItems(updated);
  };

  const handleReceive = async () => {
    const itemsToReceive = receivingItems.filter(item => 
      item.quantity_to_receive && parseFloat(item.quantity_to_receive) > 0
    );

    if (itemsToReceive.length === 0) {
      toast.error('Please enter quantity for at least one item');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      for (const item of itemsToReceive) {
        await axios.post(
          `${API}/inventory/receive`,
          {
            po_ref_number: selectedPO.po_ref_number,
            po_id: selectedPO.id,
            raw_good_type: selectedPO.raw_good_type,
            code: item.code,
            color_code: item.color_code,
            color_name: item.color_name,
            quantity_in_po: parseFloat(item.quantity),
            quantity_received: parseFloat(item.quantity_to_receive),
            unit_price: parseFloat(item.unit_price)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // Check if all items received and update PO status to EXECUTED
      const allReceived = receivingItems.every((item, index) => {
        const qtyToReceive = parseFloat(item.quantity_to_receive || 0);
        return qtyToReceive >= parseFloat(item.quantity);
      });

      if (allReceived) {
        await axios.patch(
          `${API}/purchase-orders/${selectedPO.id}/status`,
          { status: 'EXECUTED' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success('Materials received successfully');
      clearSearch();
      fetchPurchaseOrders();
      fetchExecutedPOs();
    } catch (error) {
      console.error('Error receiving materials:', error);
      toast.error(error.response?.data?.detail || 'Failed to receive materials');
    }
  };

  const approvedPOOptions = purchaseOrders.map(po => ({
    value: po.id,
    label: `${po.po_ref_number} - ${po.expected_delivery_date} (${po.raw_good_type})`
  }));

  const executedPOOptions = executedPOs
    .filter(po => !receivedPOIds.includes(po.id)) // Filter out already received
    .map(po => ({
      value: po.id,
      label: `${po.po_ref_number} - ${po.expected_delivery_date} (${po.raw_good_type})`
    }));

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/inventory')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Receive Materials
              </h1>
              <p className="text-sm text-gray-600">Receive materials from purchase orders</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Search Options */}
        <div className="bg-white border-2 border-black p-6 mb-6">
          <h2 className="text-lg font-bold mb-4 uppercase tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Search Purchase Order
          </h2>
          
          <div className="space-y-6">
            {/* Option 1: Scan PO Barcode */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">
                1. Scan PO Barcode
              </Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Barcode className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Scan PO barcode here"
                    value={barcodeSearch}
                    onChange={handleBarcodeSearch}
                    className="pl-12"
                    autoFocus
                  />
                </div>
                <Button 
                  onClick={() => searchByBarcode(barcodeSearch)}
                  className="bg-black hover:bg-gray-800"
                  disabled={!barcodeSearch}
                >
                  Search
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Scan PO barcode to automatically load the purchase order</p>
            </div>

            <div className="border-t border-gray-300"></div>

            {/* Option 2: Select from Executed POs Dropdown */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">
                2. Select from Executed PO's
              </Label>
              <SearchableSelect
                options={executedPOOptions}
                value={dropdownPO}
                onChange={handleDropdownChange}
                placeholder="Select from executed purchase orders..."
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                View executed POs ({executedPOs.length} available)
              </p>
            </div>

            {/* Clear Search Button */}
            {(barcodeSearch || dropdownPO || selectedPO) && (
              <div className="flex justify-end pt-2 border-t border-gray-300">
                <Button onClick={clearSearch} variant="outline" size="sm">
                  Clear Search
                </Button>
              </div>
            )}
          </div>
        </div>

        {selectedPO && (
          <>
            <div className="bg-white border-2 border-black p-6 mb-6">
              <h2 className="text-lg font-bold mb-4 uppercase tracking-tight">PO Details</h2>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500">PO NUMBER</p>
                  <p className="text-sm font-bold">{selectedPO.po_ref_number}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500">TYPE</p>
                  <p className="text-sm">{selectedPO.raw_good_type}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500">FUND</p>
                  <p className="text-sm">{selectedPO.fund === 'petty_cash' ? 'Petty Cash' : 'Finance Department'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500">STATUS</p>
                  <span className="inline-block px-2 py-1 text-xs font-semibold bg-green-100 text-black">
                    {selectedPO.status}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500">CREATION DATE</p>
                  <p className="text-sm">{selectedPO.date || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500">EXPECTED DELIVERY</p>
                  <p className="text-sm">{selectedPO.expected_delivery_date}</p>
                </div>
                {selectedPO.approved_date && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500">APPROVED DATE</p>
                    <p className="text-sm">{new Date(selectedPO.approved_date).toLocaleDateString()}</p>
                  </div>
                )}
                {selectedPO.executed_date && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500">EXECUTED DATE</p>
                    <p className="text-sm">{new Date(selectedPO.executed_date).toLocaleDateString()}</p>
                  </div>
                )}
                {selectedPO.created_by && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500">CREATED BY</p>
                    <p className="text-sm">{selectedPO.created_by}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white border-2 border-black overflow-hidden">
              <table className="min-w-full" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <thead className="bg-gray-50 border-b-2 border-black">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Code</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Color</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Ordered Qty</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">
                      {selectedPO?.raw_good_type === 'FABRIC LN' ? 'Price per Meter' : 'Unit Price'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Receive Qty</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase">Shortage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {receivingItems.map((item, index) => {
                    const orderedQty = parseFloat(item.quantity) || 0;
                    const receivingQty = parseFloat(item.quantity_to_receive) || 0;
                    const shortage = orderedQty - receivingQty;
                    const isFabric = selectedPO?.raw_good_type === 'FABRIC LN';
                    const priceDisplay = isFabric ? `$${item.unit_price}/m` : `$${item.unit_price}`;
                    
                    return (
                      <tr key={index}>
                        <td className="px-4 py-3 text-xs">{item.code}</td>
                        <td className="px-4 py-3 text-xs">{item.color_code} - {item.color_name}</td>
                        <td className="px-4 py-3 text-xs">{item.quantity}</td>
                        <td className="px-4 py-3 text-xs">{priceDisplay}</td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            value={item.quantity_to_receive}
                            onChange={(e) => handleQuantityChange(index, e.target.value)}
                            placeholder="Enter value"
                            max={item.quantity}
                            className="w-32"
                          />
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {receivingQty > 0 ? (
                            shortage > 0 ? (
                              <span className="text-red-600 font-medium">{shortage.toFixed(2)}</span>
                            ) : shortage === 0 ? (
                              <span className="text-green-600 font-medium">Complete</span>
                            ) : (
                              <span className="text-orange-600 font-medium">Over by {Math.abs(shortage).toFixed(2)}</span>
                            )
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="p-6 border-t-2 border-black">
                <Button onClick={handleReceive} className="bg-black hover:bg-gray-800">
                  Receive Materials
                </Button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
